/* eslint-disable no-throw-literal */
import gql from 'graphql-tag';
import apolloClient from '../../../../utils/apolloClient';

// ─── GraphQL Query ────────────────────────────────────────────────────────────
//
// search_place_invoice(input: SearchInput): PlaceInvoiceListing
//
// Pagination fields (from, size) sit at the top level of SearchInput.
// Place filtering goes into filter.place_filter.place_id (array of place IDs).
// user_id is required at the top level of SearchInput.

const SEARCH_PLACE_INVOICE = gql`
  query SearchPlaceInvoice($input: SearchInput) {
    search_place_invoice(input: $input) {
      total_pages
      total_places
      place_invoice_listing {
        place_id
        name
        address_line_1
        address_line_2
        city
        state
        country
        order_count
        sale_value
        kravein_fees
        kravein_payout
        net_payout
        place_pdf_url
        place_csv_url
        admin_place_pdf_url
        admin_place_csv_url
        audit {
          created_at
          created_by
          updated_at
          updated_by
        }
        orders {
          menu_order_id
          order_reference
          order_date
          place_name
          service_type
          order_subtotal
          order_total
          sale_value
          promo
          sub_total_gst
          venue_charges
          delivery_fee
          delivery_fee_discount
          delivery_fee_net
          adjustment_amount
          application_fee
          credit_card_fees
          kravein_charges
          kravein_fee_gst
          kravein_fees
          kravein_payout
          krv_fee
          net_payout
        }
      }
      error {
        code
        description
        object {
          id
          name
          type
        }
      }
    }
  }
`;

// ─── Runtime Validators ───────────────────────────────────────────────────────

const SERVICE_TYPES = new Set(['Pickup', 'Delivery', 'Dinein']);

const assertServiceType = (val, menuOrderId) => {
  if (!val || !SERVICE_TYPES.has(val)) {
    console.warn(
      `[searchPlaceInvoiceService] Unexpected service_type: "${val}" for order "${menuOrderId}", defaulting to "Pickup"`,
    );
    return 'Pickup';
  }
  return val;
};

// ─── Pure Transformers ────────────────────────────────────────────────────────

const toAudit = raw => ({
  createdAt: raw?.created_at ?? '',
  createdBy: raw?.created_by ?? '',
  updatedAt: raw?.updated_at ?? '',
  updatedBy: raw?.updated_by ?? '',
});

const toOrder = raw => {
  const menuOrderId = raw?.menu_order_id ?? 'unknown';
  return {
    menuOrderId,
    orderReference: raw?.order_reference ?? '',
    orderDate: raw?.order_date ?? null,
    placeName: raw?.place_name ?? '',
    serviceType: assertServiceType(raw?.service_type, menuOrderId),
    orderSubtotal: raw?.order_subtotal ?? 0,
    orderTotal: raw?.order_total ?? 0,
    saleValue: raw?.sale_value ?? 0,
    promo: raw?.promo ?? 0,
    subTotalGst: raw?.sub_total_gst ?? 0,
    venueCharges: raw?.venue_charges ?? 0,
    deliveryFee: raw?.delivery_fee ?? 0,
    deliveryFeeDiscount: raw?.delivery_fee_discount ?? 0,
    deliveryFeeNet: raw?.delivery_fee_net ?? 0,
    adjustmentAmount: raw?.adjustment_amount ?? 0,
    applicationFee: raw?.application_fee ?? 0,
    creditCardFees: raw?.credit_card_fees ?? 0,
    kraveinCharges: raw?.kravein_charges ?? 0,
    kraveinFeeGst: raw?.kravein_fee_gst ?? 0,
    kraveinFees: raw?.kravein_fees ?? 0,
    kraveinPayout: raw?.kravein_payout ?? 0,
    krvFee: raw?.krv_fee ?? 0,
    netPayout: raw?.net_payout ?? 0,
  };
};

const toPlaceInvoice = raw => {
  const placeId = raw?.place_id ?? 'unknown';
  const orders = Array.isArray(raw?.orders)
    ? raw.orders.filter(o => o !== null).map(toOrder)
    : [];
  const promo = orders.reduce((sum, o) => sum + o.promo, 0);
  const venueCharges = orders.reduce((sum, o) => sum + o.venueCharges, 0);
  return {
    placeId,
    name: raw?.name ?? '',
    addressLine1: raw?.address_line_1 ?? '',
    addressLine2: raw?.address_line_2 ?? '',
    city: raw?.city ?? '',
    state: raw?.state ?? '',
    country: raw?.country ?? '',
    orderCount: raw?.order_count ?? 0,
    saleValue: raw?.sale_value ?? 0,
    kraveinFees: raw?.kravein_fees ?? 0,
    kraveinPayout: raw?.kravein_payout ?? 0,
    netPayout: raw?.net_payout ?? 0,
    placePdfUrl: raw?.place_pdf_url ?? null,
    placeCsvUrl: raw?.place_csv_url ?? null,
    adminPlacePdfUrl: raw?.admin_place_pdf_url ?? null,
    adminPlaceCsvUrl: raw?.admin_place_csv_url ?? null,
    promo,
    venueCharges,
    audit: toAudit(raw?.audit),
    orders,
  };
};

const toPlaceInvoicePage = raw => ({
  placeInvoices: Array.isArray(raw?.place_invoice_listing)
    ? raw.place_invoice_listing
        .filter(item => item !== null)
        .map(toPlaceInvoice)
    : [],
  totalPages: raw?.total_pages ?? 0,
  totalPlaces: raw?.total_places ?? 0,
});

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === 'object' && err !== null && 'code' in err && 'message' in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('unauthorized') || msg.includes('unauthenticated')) {
      return { message: err.message, code: 'UNAUTHORIZED' };
    }
    if (msg.includes('network') || msg.includes('failed to fetch')) {
      return { message: err.message, code: 'NETWORK_ERROR' };
    }
    return { message: err.message, code: 'UNKNOWN_ERROR' };
  }
  return { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function searchPlaceInvoices(
  params,
  client = apolloClient.clientPrivate,
) {
  const { userId, from = 0, size = 10, filter = {} } = params;

  const placeFilter = {};
  if (Array.isArray(filter.placeIds) && filter.placeIds.length > 0) {
    placeFilter.place_id = filter.placeIds;
  }

  try {
    const { data } = await client.query({
      query: SEARCH_PLACE_INVOICE,
      variables: {
        input: {
          user_id: userId,
          from,
          size,
          ...(Object.keys(placeFilter).length > 0
            ? { filter: { place_filter: placeFilter } }
            : {}),
        },
      },
      fetchPolicy: 'network-only',
    });

    const result = data?.search_place_invoice;

    if (!result) {
      throw {
        message: 'No data returned from search_place_invoice',
        code: 'PARSE_ERROR',
      };
    }

    if (result.error?.length) {
      throw {
        message: result.error[0]?.description ?? 'API error',
        code: 'API_ERROR',
      };
    }

    if (!Array.isArray(result.place_invoice_listing)) {
      throw {
        message: 'No data returned from search_place_invoice',
        code: 'PARSE_ERROR',
      };
    }

    return toPlaceInvoicePage(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

// ─── Export Pure Transformers ─────────────────────────────────────────────────

export { toPlaceInvoice, toPlaceInvoicePage };

// ─── Export Service ───────────────────────────────────────────────────────────

export const searchPlaceInvoiceService = {
  searchPlaceInvoices,
};
